import { useState, type ReactNode } from 'react';

import { STARTER_SET, type StarterFeed } from '@feedreader/shared';

/**
 * The discovery track (M2.7.2, PRD R1.2 and R1.3).
 *
 * ## Two tracks, neither assuming the other
 *
 * R1.2 is specific: OPML import for migrators, guided discovery for people
 * with nothing to import, and **the word "OPML" never appears on the discovery
 * track**. It does not appear here. Import itself is M2.2; until then the
 * second door is "paste an address", which is R1.3 — people think in websites,
 * not feed URLs, and the discovery in M1.8.1 turns one into the other.
 *
 * ## Nothing is pre-selected
 *
 * A pre-ticked list is a recommendation wearing a checkbox, and it produces
 * accounts full of feeds nobody chose. PRD §3.2's attention contract applies
 * to onboarding as much as to the timeline: the reader decides what arrives.
 *
 * ## Skipping is a supported outcome, not a failure
 *
 * Somebody who wants to add their own three feeds and nothing else should be
 * able to leave in one click, and reach exactly the empty state that existed
 * before this screen did.
 */

export interface OnboardingProps {
  /** Subscribes to the chosen feeds. Resolves when all have been attempted. */
  readonly onChoose: (
    feeds: readonly StarterFeed[],
    categories: readonly string[],
  ) => Promise<void>;
  readonly onAddOwn: () => void;
  readonly onSkip: () => void;
}

export function Onboarding(props: OnboardingProps): ReactNode {
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [open, setOpen] = useState<string>(STARTER_SET[0]?.id ?? '');
  const [busy, setBusy] = useState(false);

  const toggle = (url: string): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(url)) {
        next.delete(url);
      } else {
        next.add(url);
      }
      return next;
    });
  };

  const chosen = STARTER_SET.flatMap((category) =>
    category.feeds.filter((feed) => selected.has(feed.url)),
  );

  const chosenCategories = STARTER_SET.filter((category) =>
    category.feeds.some((feed) => selected.has(feed.url)),
  ).map((category) => category.id);

  return (
    <main className="onboarding">
      <div className="onboarding__inner">
        <h1 className="onboarding__title">What would you like to read?</h1>
        <p className="onboarding__lead">
          Pick a few to start with. You can add your own at any time, and remove anything
          you do not want.
        </p>

        <div className="onboarding__categories">
          {STARTER_SET.map((category) => {
            const expanded = open === category.id;
            const count = category.feeds.filter((feed) => selected.has(feed.url)).length;

            return (
              <section
                className={`starter${expanded ? ' is-open' : ''}`}
                key={category.id}
              >
                <h2 className="starter__heading">
                  <button
                    className="starter__toggle"
                    aria-expanded={expanded}
                    onClick={() => {
                      setOpen(expanded ? '' : category.id);
                    }}
                  >
                    <span aria-hidden="true" className="starter__twisty">
                      {expanded ? '▾' : '▸'}
                    </span>
                    <span className="starter__name">{category.name}</span>
                    {count > 0 && <span className="starter__count">{count}</span>}
                  </button>
                </h2>
                <p className="starter__description">{category.description}</p>

                {expanded && (
                  <ul className="starter__feeds">
                    {category.feeds.map((feed) => (
                      <li key={feed.url}>
                        <label className="starter__feed">
                          <input
                            type="checkbox"
                            checked={selected.has(feed.url)}
                            onChange={() => {
                              toggle(feed.url);
                            }}
                          />
                          <span className="starter__feed-title">{feed.title}</span>
                          <span className="starter__feed-description">
                            {feed.description}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>

        <div className="onboarding__actions">
          <button
            className="button button--primary"
            disabled={busy || chosen.length === 0}
            onClick={() => {
              setBusy(true);
              void props.onChoose(chosen, chosenCategories).finally(() => {
                setBusy(false);
              });
            }}
          >
            {busy
              ? 'Adding…'
              : chosen.length === 0
                ? 'Choose some feeds'
                : `Add ${String(chosen.length)} ${chosen.length === 1 ? 'feed' : 'feeds'}`}
          </button>

          <button className="button" onClick={props.onAddOwn} disabled={busy}>
            I have my own
          </button>

          <button className="link" onClick={props.onSkip} disabled={busy}>
            Skip for now
          </button>
        </div>

        {busy && (
          <p className="onboarding__note" role="status">
            Fetching each one and reading its most recent items. This takes a moment.
          </p>
        )}
      </div>
    </main>
  );
}
